<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="field-modal">
  
      <woot-modal-header :header-title="isEditing ? $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.EDIT_TITLE') : $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.ADD_TITLE')" />
      <form @submit.prevent="onSubmit" class="modal-form">
        <!-- Field Name -->
        <div class="form-row">
          <woot-input
            v-model="formData.field_name"
            :label="$t('AI_SETTINGS.DATA_COLLECTION.FIELDS.FIELD_NAME')"
            :required="true"
            :placeholder="$t('AI_SETTINGS.DATA_COLLECTION.FIELDS.FIELD_NAME_PLACEHOLDER')"
            :error="fieldNameError"
            :class="{ error: fieldNameError }"
            maxlength="50"
            @input="validateFieldName"
            @blur="validateFieldName"
          />
        </div>
        
        <!-- Display Name -->
        <div class="form-row">
          <woot-input
            v-model="formData.display_name"
            :label="$t('AI_SETTINGS.DATA_COLLECTION.FIELDS.DISPLAY_NAME')"
            :required="true"
            :placeholder="$t('AI_SETTINGS.DATA_COLLECTION.FIELDS.DISPLAY_NAME_PLACEHOLDER')"
            :error="displayNameError"
            :class="{ error: displayNameError }"
            @input="validateDisplayName"
            @blur="validateDisplayName"
          />
        </div>
        
        <!-- Description -->
        <div class="form-row">
          <label class="form-label text-heading">{{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.DESCRIPTION') }}</label>
          <textarea
            v-model="formData.description"
            rows="3"
            class="form-input"
            :placeholder="$t('AI_SETTINGS.DATA_COLLECTION.FIELDS.DESCRIPTION_PLACEHOLDER')"
          ></textarea>
        </div>
        
        <!-- Field Type -->
        <div class="form-row">
          <label class="form-label text-heading">{{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.FIELD_TYPE') }}</label>
          <select v-model="formData.field_type" class="form-input" required>
            <option v-for="type in fieldTypes" :key="type.value" :value="type.value">
              {{ type.label }}
            </option>
          </select>
          <p class="text-xs text-gray-600 mt-1">{{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.FIELD_TYPE_HINT') }}</p>
        </div>
        
        <!-- Required Toggle -->
        <div class="form-row">
          <label class="form-checkbox">
            <input v-model="formData.is_required" type="checkbox" />
            <span class="checkmark"></span>
            <span class="text-heading">{{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.IS_REQUIRED') }}</span>
          </label>
        </div>
        
        <!-- Enabled Toggle -->
        <div class="form-row">
          <label class="form-checkbox">
            <input v-model="formData.is_enabled" type="checkbox" />
            <span class="checkmark"></span>
            <span class="text-heading">{{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.IS_ENABLED') }}</span>
          </label>
        </div>
        
        <div class="modal-footer">
          <woot-button
            type="button"
            variant="smooth"
            color-scheme="ghost"
            @click="onClose"
          >
            {{ $t('AI_SETTINGS.CANCEL') }}
          </woot-button>
          
          <woot-button
            type="submit"
            variant="smooth"
            color-scheme="success"
            :is-loading="isSaving"
            :disabled="isSubmitDisabled"
          >
            {{ isEditing ? $t('AI_SETTINGS.UPDATE') : $t('AI_SETTINGS.CREATE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<script>
import { FIELD_TYPES } from '../constants';

export default {
  name: 'FieldModal',
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    field: {
      type: Object,
      default: null
    }
  },
  
  emits: ['close', 'save'],
  
  data() {
    return {
      isSaving: false,
      fieldTypes: FIELD_TYPES,
      formData: {
        field_name: '',
        display_name: '',
        description: '',
        field_type: 'text',
        is_required: false,
        is_enabled: true
      },
      fieldNameError: '',
      displayNameError: ''
    }
  },
  
  computed: {
    isEditing() {
      return !!this.field
    },
    isSubmitDisabled() {
      return !!this.fieldNameError || !!this.displayNameError || !this.formData.field_name || !this.formData.display_name
    }
  },
  
  watch: {
    field: {
      handler(newField) {
        if (newField) {
          this.formData = { ...newField }
        } else {
          this.resetForm()
        }
      },
      immediate: true
    }
  },
  
  methods: {
    onClose() {
      this.$emit('close')
    },
    
    async onSubmit() {
      if (this.isSaving) return
      
      this.validateFieldName()
      this.validateDisplayName()
      if (this.fieldNameError || this.displayNameError) return
      
      try {
        this.isSaving = true
        this.$emit('save', { ...this.formData })
      } catch (error) {
        console.error('Error in form submission:', error)
      } finally {
        this.isSaving = false
      }
    },
    
    resetForm() {
      this.formData = {
        field_name: '',
        display_name: '',
        description: '',
        field_type: 'text',
        is_required: false,
        is_enabled: true
      }
      this.fieldNameError = ''
      this.displayNameError = ''
    },
    
    validateDisplayName() {
      const value = (this.formData.display_name || '').trim()
      if (!value) {
        this.displayNameError = this.getRequiredFieldError('AI_SETTINGS.DATA_COLLECTION.FIELDS.DISPLAY_NAME')
        return
      }
      this.displayNameError = ''
    },
    
    validateFieldName() {
      const value = (this.formData.field_name || '').trim()
      if (!value) {
        this.fieldNameError = this.getRequiredFieldError('AI_SETTINGS.DATA_COLLECTION.FIELDS.FIELD_NAME')
        return
      }
      if (value.length > 50) {
        this.fieldNameError = this.$t('AI_SETTINGS.DATA_COLLECTION.FIELDS.VALIDATION.MAX_LENGTH')
        return
      }
      const pattern = /^[a-z][a-z0-9_]*$/
      if (!pattern.test(value)) {
        this.fieldNameError = this.$t('AI_SETTINGS.DATA_COLLECTION.FIELDS.VALIDATION.PATTERN')
        return
      }
      this.fieldNameError = ''
    },
    
    getRequiredFieldError(labelKey) {
      return this.$t('GENERAL.ERRORS.REQUIRED_FIELD', {
        field: this.$t(labelKey),
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.field-modal {
  width: 100%;
  max-width: 600px;
}

.modal-header {
  margin-bottom: var(--space-large);
  
  h2 {
    margin: 0;
    color: var(--color-heading);
    font-size: var(--font-size-large);
  }
}

.modal-form {
  .form-row {
    margin-bottom: var(--space-normal);
    
    .form-label {
      @apply text-slate-700 dark:text-slate-300
    };

    .form-checkbox {
      display: flex;
      align-items: center;
      cursor: pointer;
      
      input[type="checkbox"] {
        margin-right: var(--space-small);
      }
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: center;
  gap: var(--space-small);
  margin-top: var(--space-large);
  padding-top: var(--space-normal);
  border-top: 1px solid var(--color-border-light);
}

@media (max-width: 768px) {
  .field-modal {
    max-width: 100%;
    margin: var(--space-small);
  }
  
  .modal-footer {
    flex-direction: column-reverse;
    
    .woot-button {
      width: 100%;
    }
  }
}
</style>
